import React, { FunctionComponent } from 'react';
import { QueryClient, useQuery, QueryClientProvider } from '@tanstack/react-query';

import type { View } from 'common/types/view';

import { I18n } from 'common/i18n';
import airbrake from 'common/airbrake';
import ceteraUtils from 'common/cetera/utils';
import {
  deleteView,
  deleteRevision,
  deleteStoryDraft,
  fetchViewsThatNeedToBeDeletedToDeleteView,
} from 'common/views/delete_helpers';
import {
  isDataset,
  isDerivedReport,
  isDerivedView,
  isMeasure,
  isStory,
  isVisualizationCanvas,
} from 'common/views/view_types';
import { assetIsDraft } from 'common/views/helpers';

import { DeleteAssetModalContent } from './DeleteAssetModalContent';

import './index.scss';
import { ForgeButton, ForgeDialog, ForgeScaffold, ForgeToolbar } from '@tylertech/forge-react';

export const translationScope = 'shared.asset_browser.result_list_table.action_modal.delete_asset';

export interface DeleteAssetModalProps {
  /** View to delete */
  view: View;

  /**
   * DSMAPI revision sequence.
   * If this is provided, then the revision is what will be deleted by this modal.
   * If this is NOT provided, and `assetIsStoryDraft` is `false`, then the base view is what is deleted.
   */
  revisionSeq?: number;

  /**
   * Whether or not we're deleting a story draft.
   * If this is `true`, then instead of deleting the view from core we hit storyteller to tell it to delete the draft.
   */
  assetIsStoryDraft: boolean;

  /**
   * Called when the user clicks Cancel (or presses ESC, or clicks outside modal).
   */
  onDismiss: () => any;

  /**
   * When an asset was deleted, we call back on this.
   * It's up to the user of the component to decide
   * what to do.
   */
  onDeleted: () => any;
}

/**
 * If the asset is a dataset (`viewType` is `tabular` and `displayType` is `table`)
 * then we hit the catalog to see how many assets are derived off of it that will ALSO
 * be deleted when it is deleted...
 *
 * Note that this is not an exact science. Some assets, like stories, embed datasets but
 * the catalog is not aware of this. The stories themselves will still work, but the embedded
 * datasets will not!
 *
 * @param view View to get derived view count for
 * @param revisionSeq Revision sequence... if this is present, then this function will return undefined
 */
export const fetchNumberOfDerivedViews = async (
  view: View,
  revisionSeq?: number,
): Promise<number | undefined> => {
  // if we're looking at something that's not a dataset or something that's a draft,
  // don't fetch this number... this number is only for warning when children are also going to be deleted,
  // which currently doesn't actually happen for non-dataset assets and for draft-like assets
  if (!isDataset(view) || assetIsDraft({ coreView: view, isRevision: revisionSeq !== undefined })) {
    return Promise.resolve(undefined);
  }

  const childAssetsResponse = await ceteraUtils.query({ derivedFrom: view.id });

  if (childAssetsResponse?.resultSetSize === undefined) {
    airbrake.notify({
      error: `DeleteAssetModal could not find get assets derived from ${view.id}`,
      context: { component: 'DeleteAssetModal' },
    });
    return Promise.reject(undefined);
  }

  return Promise.resolve(childAssetsResponse.resultSetSize);
};

/**
 * Delete an asset (a view in core), a revision (a draft-like asset in DSMAPI), or a story draft (in storyteller)
 *
 * @param id UID (4x4) of the asset to delete
 * @param revisionSeq Revision sequence... if this is present, then DSMAPI will be hit to delete the revision
 * @param assetIsStoryDraft Whether or not this is a story draft... if this is true, then storyteller will be hit to delete the draft
 */
export const deleteAsset = async (id: string, revisionSeq?: number, assetIsStoryDraft?: boolean) => {
  try {
    // revisionSeq can be 0 which will evaluate to false :)
    if (revisionSeq !== undefined) {
      await deleteRevision(id, revisionSeq);
    } else if (assetIsStoryDraft) {
      await deleteStoryDraft(id);
    } else {
      await deleteView(id);
    }
    // Return success response for React Query
    return { success: true, id };
  } catch (e) {
    airbrake.notify({
      error: `DeleteAssetModal could not delete ${id} (revisionSeq: ${revisionSeq}, assetIsStoryDraft: ${assetIsStoryDraft})`,
      context: { component: 'DeleteAssetModal' },
    });
    // Re-throw to let React Query handle the error
    throw e;
  }
};

/**
 * This modal does many things.
 *
 * It...
 * - Fetches the number of views that the given view has derived off of it to let the user know how many things they're deleting
 * - Checks if any other views are blocking deletion of the view (i.e. data federated derived views)
 * - Talks to DSMAPI to delete revisions
 * - Talks to storyteller to delete story drafts
 * - Talks to core to delete everything else :)
 */
const DeleteAssetModal: FunctionComponent<DeleteAssetModalProps> = ({
  view,
  revisionSeq,
  assetIsStoryDraft,
  onDismiss,
  onDeleted,
}) => {
  // Query 1: Fetch count of derived views (child assets) that depend on this dataset
  // Only executed for datasets (not drafts) to warn users about cascading deletions
  const {
    isLoading: isDerivedViewsQueryLoading,
    isError: hasDerivedViewsQueryError,
    data: derivedViewCount,
    fetchStatus: derivedViewsQueryFetchStatus,
  } = useQuery(
    ['derivedViews', view.id, revisionSeq, assetIsStoryDraft],
    async () => await fetchNumberOfDerivedViews(view, revisionSeq),
    {
      // keep the value cached for 30 seconds
      staleTime: 30 * 1000,
      enabled: Boolean(
        view?.id &&
          isDataset(view) &&
          !assetIsDraft({ coreView: view, isRevision: revisionSeq !== undefined }),
      ),
    },
  );

  // Query 2: Check for external dependencies that would prevent deletion
  // This identifies federated assets on other domains that depend on this asset
  const {
    isLoading: isBlockingViewsQueryLoading,
    isError: hasBlockingViewsQueryError,
    data: blockingViewsData,
  } = useQuery(
    ['viewsBlockingDeletion', view.id, revisionSeq, assetIsStoryDraft],
    async () => await fetchViewsThatNeedToBeDeletedToDeleteView(view.id, revisionSeq, assetIsStoryDraft),
    {
      // keep the value cached for 30 seconds
      staleTime: 30 * 1000,
    },
  );

  // Query 3: Performs the actual deletion when triggered
  // Disabled by default, only runs when user confirms deletion via runDeleteAssetQuery()
  const {
    isLoading: isDeletionQueryLoading,
    isError: hasDeletionError,
    isSuccess: isDeletionComplete,
    refetch: executeAssetDeletion,
    fetchStatus: deletionQueryFetchStatus,
  } = useQuery(
    ['deleteAsset', view.id],
    async () => await deleteAsset(view.id, revisionSeq, assetIsStoryDraft),
    {
      // so that this is run by calling the `refetch` function instead of when the component mounts
      enabled: false,

      // don't retry if this fails, and don't cache the response
      retry: false,
      cacheTime: 0,

      onSuccess: onDeleted,
    },
  );

  // React Query v4 fix: isLoading is true even for disabled queries
  // We must check fetchStatus === 'fetching' to determine if query is actually running
  const isActivelyDeleting = isDeletionQueryLoading && deletionQueryFetchStatus === 'fetching';
  const isActivelyLoadingDerivedViews =
    isDerivedViewsQueryLoading && derivedViewsQueryFetchStatus === 'fetching';

  // Overall loading state: true if any query is actively fetching or deletion just completed
  // Includes isDeletionComplete to maintain loading UI during page refresh after successful deletion
  const isModalBusy =
    isActivelyLoadingDerivedViews || isBlockingViewsQueryLoading || isActivelyDeleting || isDeletionComplete;

  // Aggregate error state: true if any query failed
  const hasAnyQueryError = hasDerivedViewsQueryError || hasBlockingViewsQueryError || hasDeletionError;

  // Check if deletion is blocked by external dependencies
  const isDeletionBlockedByDependencies = !!blockingViewsData?.hasViewsBlockingDeletion;

  const isDraft = assetIsDraft({
    coreView: view,
    isRevision: revisionSeq !== undefined,
    isDraftStory: assetIsStoryDraft,
  });

  // Disable modal dismissal while operations are in progress
  const handleModalDismiss = isModalBusy ? () => {} : onDismiss;

  function getModalTitle() {
    const prefix = `${translationScope}.titles`;
    if (isDeletionBlockedByDependencies) {
      return I18n.t(`${translationScope}.delete_blocked_by_child_views.title`);
    }
    if (isDraft) {
      return I18n.t(`${prefix}.draft`);
    }
    if (isDerivedReport(view)) {
      return I18n.t(`${prefix}.report_view`);
    }
    if (isStory(view)) {
      return I18n.t(`${prefix}.story`);
    }
    if (isMeasure(view)) {
      return I18n.t(`${prefix}.measure`);
    }
    if (isVisualizationCanvas(view)) {
      return I18n.t(`${prefix}.visualization`);
    }
    if (isDerivedView(view)) {
      return I18n.t(`${prefix}.view`);
    }
    if (isDataset(view)) {
      return I18n.t(`${prefix}.dataset`);
    }
    return I18n.t(`${prefix}.delete`);
  }

  return (
    <ForgeDialog
      className="delete-asset"
      open={true}
      role="dialog"
      on-forge-dialog-close={handleModalDismiss}
      data-testid="confirm-delete-asset-dialog"
    >
      <ForgeScaffold>
        <ForgeToolbar no-divider slot="header">
          <h4 slot="start" className="forge-typography--subheading4 forge-header-title">
            {getModalTitle()}
          </h4>
        </ForgeToolbar>
        <div slot="body">
          <DeleteAssetModalContent
            view={view}
            busy={isActivelyLoadingDerivedViews || isBlockingViewsQueryLoading}
            hasError={hasAnyQueryError}
            isDraft={isDraft}
            derivedViewCount={derivedViewCount}
            viewsBlockingDeletion={blockingViewsData}
          />
        </div>
        <ForgeToolbar slot="footer">
          <ForgeButton
            slot="end"
            variant="outlined"
            data-testid="cancel-delete-asset-button"
            onClick={onDismiss}
            disabled={isModalBusy}
          >
            {I18n.t(`${translationScope}.cancel`)}
          </ForgeButton>
          {!hasAnyQueryError && !isDeletionBlockedByDependencies && (
            <ForgeButton
              slot="end"
              variant="raised"
              theme="error"
              data-testid="delete-asset-button"
              onClick={() => executeAssetDeletion()}
              busy={isModalBusy}
              disabled={isModalBusy || hasAnyQueryError}
            >
              {I18n.t(`${translationScope}.delete`)}
            </ForgeButton>
          )}
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
};

const queryClient = new QueryClient();

const QueryClientWrappedDeleteAsset = (props: DeleteAssetModalProps) => (
  <QueryClientProvider client={queryClient}>
    <DeleteAssetModal {...props} />
  </QueryClientProvider>
);

export default QueryClientWrappedDeleteAsset;
